/**
 * Configuration — Support Tickets — workflow helpers.
 * Spec: docs/specs/configuration-support-tickets.md §9/§10.
 */

import { getActorDisplayName, isTeamCoOwnerActor, isTeamOwnerActor } from './teamConfig';
import {
  MESSAGES,
  SUPPORT_TICKET_MODULE,
  VALIDATION_MESSAGES,
  VISITOR_ENTRY_POINT,
  collectSupportTicketConfigValidationErrors,
  nextSupportTicketRuleId,
  normalizeSupportTicketConfiguration,
  supportTicketConfigHasChanges,
} from './supportTicketConfig';
import { emitRuleSaveHistory } from './changeHistoryEmitters';

export { MESSAGES, VALIDATION_MESSAGES };

/** View, Add and Edit require current permissions (§1) — Owner / Co-owner, matching sibling Configuration rules. */
export function canAccessSupportTicketConfiguration(auth) {
  return isTeamOwnerActor(auth) || isTeamCoOwnerActor(auth);
}

export function canViewSupportTicketConfiguration(auth) {
  return canAccessSupportTicketConfiguration(auth);
}

export function canAddSupportTicketConfiguration(auth) {
  return canAccessSupportTicketConfiguration(auth);
}

export function canEditSupportTicketConfiguration(auth) {
  return canAccessSupportTicketConfiguration(auth);
}

export function canSaveSupportTicketConfiguration(auth) {
  return canAccessSupportTicketConfiguration(auth);
}

export function buildSupportTicketConfigActor(auth) {
  if (!auth) return { name: 'Saim Sahi', accountId: '—' };
  return { name: getActorDisplayName(auth) || 'Saim Sahi', accountId: '—' };
}

/** Visitor Entry Point is always the fixed shared value. */
export function getVisitorEntryPointDisplay() {
  return VISITOR_ENTRY_POINT;
}

/**
 * Save a proposed Support Ticket Rule (Add or Edit) per §10.
 *
 * - Successful Add: assigns the permanent STK-####, saves the Rule and one
 *   Change History Add entry together.
 * - Successful changed Edit: keeps Rule ID / scope / creation metadata,
 *   saves the changed definition and one Change History Edit entry.
 * - Unchanged Save: keeps saved values, creates no Change History entry.
 * - Failed Save: nothing is saved; Screen 2 keeps all entered values.
 */
export function saveSupportTicketConfiguration(
  allConfigurations,
  draft,
  actor,
  { isAdd = false, baseline = null, openedLastUpdated = null, canSave = true } = {},
) {
  const errors = collectSupportTicketConfigValidationErrors(draft, {
    isAdd,
    baseline,
    allConfigurations,
    openedLastUpdated,
    canSave,
  });

  if (Object.keys(errors).length > 0) {
    return {
      configurations: allConfigurations,
      success: false,
      message: MESSAGES.saveFailed,
      errors,
      noChange: false,
    };
  }

  const actorName = actor?.name || 'Saim Sahi';
  const nowIso = new Date().toISOString();

  if (isAdd) {
    const id = nextSupportTicketRuleId(allConfigurations);
    const created = normalizeSupportTicketConfiguration({
      ...draft,
      id,
      dateAdded: nowIso,
      addedBy: actorName,
      lastUpdated: nowIso,
      lastUpdatedBy: actorName,
    });

    try {
      emitRuleSaveHistory({
        isNew: true,
        next: { ...created, changeReason: draft?.changeReason },
        module: SUPPORT_TICKET_MODULE,
        sourceModuleRef: 'support_ticket_configuration',
        changedItem: created.ruleName || created.ticketType || created.id,
        itemId: created.id,
        changeReason: draft?.changeReason,
        actor: actorName,
        stamp: nowIso,
        compareKeys: [
          'ruleName',
          'description',
          'ticketType',
          'visitorAvailable',
          'customerAvailable',
          'businessAvailable',
          'customerEntryPoints',
          'businessEntryPoints',
          'automaticPriority',
        ],
      });
    } catch {
      return {
        configurations: allConfigurations,
        success: false,
        message: MESSAGES.saveFailed,
        errors: { form: VALIDATION_MESSAGES.saveFailed },
        noChange: false,
      };
    }

    return {
      configurations: [...allConfigurations, created],
      config: created,
      success: true,
      message: MESSAGES.saved,
      errors: {},
      noChange: false,
    };
  }

  const existing = (allConfigurations || []).find((row) => row.id === baseline?.id);
  if (!existing) {
    return {
      configurations: allConfigurations,
      success: false,
      message: MESSAGES.notFound,
      errors: { form: VALIDATION_MESSAGES.requiredCheckIncomplete },
      noChange: false,
    };
  }

  const nextDraft = normalizeSupportTicketConfiguration({
    ...existing,
    ...draft,
    id: existing.id,
    module: SUPPORT_TICKET_MODULE,
    ticketType: existing.ticketType,
    dateAdded: existing.dateAdded,
    addedBy: existing.addedBy,
  });

  if (!supportTicketConfigHasChanges(existing, nextDraft)) {
    return {
      configurations: allConfigurations,
      config: existing,
      success: true,
      message: MESSAGES.noChange,
      errors: {},
      noChange: true,
    };
  }

  const updated = {
    ...nextDraft,
    lastUpdated: nowIso,
    lastUpdatedBy: actorName,
  };

  try {
    emitRuleSaveHistory({
      isNew: false,
      previous: existing,
      next: { ...updated, changeReason: draft?.changeReason },
      module: SUPPORT_TICKET_MODULE,
      sourceModuleRef: 'support_ticket_configuration',
      changedItem: updated.ruleName || updated.ticketType || updated.id,
      itemId: updated.id,
      changeReason: draft?.changeReason,
      actor: actorName,
      stamp: nowIso,
      compareKeys: [
        'ruleName',
        'description',
        'visitorAvailable',
        'customerAvailable',
        'businessAvailable',
        'customerEntryPoints',
        'businessEntryPoints',
        'automaticPriority',
      ],
    });
  } catch {
    return {
      configurations: allConfigurations,
      success: false,
      message: MESSAGES.saveFailed,
      errors: { form: VALIDATION_MESSAGES.saveFailed },
      noChange: false,
    };
  }

  return {
    configurations: allConfigurations.map((row) => (row.id === updated.id ? updated : row)),
    config: updated,
    success: true,
    message: MESSAGES.saved,
    errors: {},
    noChange: false,
  };
}
